// @tennis-matches/services/masterdata/people
// Shared club people (Spieler/Personen) across apps.

import { getSupabaseClient } from "../supabase/index.js";
import {
  isMissingRestResourceError,
  isRestEndpointUnavailable,
  markRestEndpointUnavailable,
} from "../supabase/rest-endpoint-availability.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "../hub/hub-scoped-cache.js";
import { syncBillingContactFromClubPersonMirror } from "./billing-contact-club-person-sync.js";
import { extractClubPersonBillingAssignmentReason } from "./club-person-billing-assignment-errors.js";
import { assertNoDuplicateClubPersonName } from "./club-person-name-match.js";
import { CLUB_PERSON_CONTACT_SOURCE_TRAINER } from "./contact-source.js";

const RPC_CLUB_PEOPLE_UPDATE_TRACKED = "rpc:club_people_update_tracked";

function __asStr(v) {
  return String(v ?? "").trim();
}

const CLUB_PEOPLE_PROTECTED_PERSIST_COLUMNS = new Set([
  "id",
  "first_name",
  "last_name",
  "tenant_id",
]);

const CLUB_PEOPLE_LOAD_PAGE = 1000;
const CLUB_PEOPLE_LOAD_HARD_MAX = 20000;
const CLUB_PEOPLE_BY_ID_CHUNK = 80;

function extractMissingClubPeopleColumn(error) {
  const msg = String(error?.message || error?.details || "");
  const m = /Could not find the '([^']+)' column of 'club_people'/i.exec(msg);
  return m ? String(m[1]).trim() : "";
}

export function omitClubPeopleColumnForSchemaRetry(payload, column) {
  const col = __asStr(column);
  if (!col || !payload || typeof payload !== "object") return payload;
  if (CLUB_PEOPLE_PROTECTED_PERSIST_COLUMNS.has(col)) return payload;
  if (!(col in payload)) return payload;
  const next = { ...payload };
  delete next[col];
  return next;
}

const CLUB_PEOPLE_SCHEMA_RETRY_MAX = 12;

async function persistClubPersonWithSchemaRetry(payload, run) {
  let current = { ...payload };
  for (let attempt = 0; attempt < CLUB_PEOPLE_SCHEMA_RETRY_MAX; attempt += 1) {
    const { data, error } = await run(current);
    if (!error) return { data, error: null };
    const missingCol = extractMissingClubPeopleColumn(error);
    if (!missingCol) return { data, error };
    const next = omitClubPeopleColumnForSchemaRetry(current, missingCol);
    if (next === current) return { data, error };
    current = next;
  }
  return {
    data: null,
    error: {
      code: "club-people-schema-retry-exhausted",
      message: "club-people-schema-retry-exhausted",
    },
  };
}

const CLUB_PEOPLE_CACHE_MS = 15000;
/** @type {Map<string, Promise<{ok: boolean, data?: Array, error?: any}>>} */
const __clubPeopleInflight = new Map();
/** @type {Map<string, { ts: number, value: {ok: boolean, data?: Array, error?: any} }>} */
const __clubPeopleCache = new Map();

export function clearClubPeopleCache() {
  __clubPeopleCache.clear();
  __clubPeopleInflight.clear();
}

registerHubCacheClear(clearClubPeopleCache);

/**
 * Load all people for the current user/club context (RLS decides).
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<{ok: boolean, data?: Array, error?: any}>}
 */
export async function loadClubPeople(options = {}) {
  const force = options?.force === true;
  const scope = await resolveHubReadScope();
  const cacheKey = scope.key;
  const now = Date.now();
  if (!force) {
    const cached = __clubPeopleCache.get(cacheKey);
    if (cached && now - cached.ts < CLUB_PEOPLE_CACHE_MS) {
      return cached.value;
    }
    const inflight = __clubPeopleInflight.get(cacheKey);
    if (inflight) return inflight;
  }

  const started = { ...scope };
  const run = (async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return { ok: false, error: "no-supabase" };

      const all = [];
      for (
        let from = 0;
        from < CLUB_PEOPLE_LOAD_HARD_MAX;
        from += CLUB_PEOPLE_LOAD_PAGE
      ) {
        const to = from + CLUB_PEOPLE_LOAD_PAGE - 1;
        const { data, error } = await supabase
          .from("club_people")
          .select("*")
          .order("last_name", { ascending: true })
          .order("first_name", { ascending: true })
          .range(from, to);
        if (error) return { ok: false, error };
        const page = data || [];
        all.push(...page);
        if (page.length < CLUB_PEOPLE_LOAD_PAGE) break;
      }
      return { ok: true, data: all };
    } catch (e) {
      return { ok: false, error: e };
    }
  })();

  const promise = run
    .then((value) => {
      if (value?.ok && isHubReadScopeCurrent(started)) {
        __clubPeopleCache.set(cacheKey, { ts: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (__clubPeopleInflight.get(cacheKey) === promise) {
        __clubPeopleInflight.delete(cacheKey);
      }
    });
  __clubPeopleInflight.set(cacheKey, promise);
  return promise;
}

export async function loadClubPeopleByIds(ids) {
  const list = [
    ...new Set((Array.isArray(ids) ? ids : []).map(__asStr).filter(Boolean)),
  ];
  if (!list.length) return { ok: true, data: [] };
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const all = [];
    for (let i = 0; i < list.length; i += CLUB_PEOPLE_BY_ID_CHUNK) {
      const chunk = list.slice(i, i + CLUB_PEOPLE_BY_ID_CHUNK);
      const { data, error } = await supabase
        .from("club_people")
        .select("*")
        .in("id", chunk);
      if (error) return { ok: false, error };
      all.push(...(data || []));
    }
    return { ok: true, data: all };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function mergeClubPeopleWithMissingIds(people, extraIds) {
  const current = Array.isArray(people) ? people : [];
  const have = new Set(current.map((p) => __asStr(p?.id)).filter(Boolean));
  const missing = [
    ...new Set(
      (Array.isArray(extraIds) ? extraIds : [])
        .map(__asStr)
        .filter((id) => id && !have.has(id)),
    ),
  ];
  if (!missing.length) return { ok: true, data: current };
  const extra = await loadClubPeopleByIds(missing);
  if (!extra.ok) return { ok: true, data: current };
  return { ok: true, data: [...current, ...(extra.data || [])] };
}

export async function createClubPerson(person, options = {}) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const skipNameCheck =
      options?.skipNameDuplicateCheck === true ||
      __asStr(person?.contact_source) === CLUB_PERSON_CONTACT_SOURCE_TRAINER;
    if (!skipNameCheck) {
      const dup = await assertNoDuplicateClubPersonName(person, options);
      if (!dup.ok) return dup;
    }

    const wantedFirst = __asStr(person?.first_name);
    const wantedLast = __asStr(person?.last_name);

    const { data, error } = await persistClubPersonWithSchemaRetry(
      person,
      (payload) =>
        supabase.from("club_people").insert([payload]).select().single(),
    );
    if (error) return { ok: false, error };

    const gotFirst = __asStr(data?.first_name);
    const gotLast = __asStr(data?.last_name);
    if (
      (wantedFirst && gotFirst !== wantedFirst) ||
      (wantedLast && gotLast !== wantedLast)
    ) {
      const pid = __asStr(data?.id) || __asStr(person?.id);
      const patched = await updateClubPerson(pid, {
        ...(wantedFirst ? { first_name: wantedFirst } : {}),
        ...(wantedLast ? { last_name: wantedLast } : {}),
      });
      if (patched.ok) return patched;
    }

    clearClubPeopleCache();
    return {
      ok: true,
      data: {
        ...data,
        first_name: gotFirst || wantedFirst || data?.first_name,
        last_name: gotLast || wantedLast || data?.last_name,
      },
    };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function updateClubPerson(id, updates, options = {}) {
  const pid = __asStr(id);
  if (!pid) return { ok: false, error: "missing-id" };
  const changedVia = __asStr(options?.changedVia) || "admin_web";
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    if (!isRestEndpointUnavailable(RPC_CLUB_PEOPLE_UPDATE_TRACKED)) {
      const { data, error } = await supabase.rpc("club_people_update_tracked", {
        p_person_id: pid,
        p_updates: updates && typeof updates === "object" ? updates : {},
        p_changed_via: changedVia,
      });

      if (!error && data && typeof data === "object" && data.ok !== false) {
        const person = data.person || data.data || data;
        await syncBillingContactFromClubPersonMirror(person);
        clearClubPeopleCache();
        return { ok: true, data: person };
      }

      if (error && isMissingRestResourceError(error)) {
        markRestEndpointUnavailable(RPC_CLUB_PEOPLE_UPDATE_TRACKED);
      } else if (error) {
        const reason = extractClubPersonBillingAssignmentReason(error);
        return reason ? { ok: false, error, reason } : { ok: false, error };
      } else if (data && typeof data === "object" && data.ok === false) {
        const reason =
          extractClubPersonBillingAssignmentReason(data) ||
          __asStr(data.reason);
        return { ok: false, error: data, reason: reason || undefined };
      }
    }

    const { data: fallbackData, error: fallbackError } =
      await persistClubPersonWithSchemaRetry(updates, (payload) =>
        supabase
          .from("club_people")
          .update(payload)
          .eq("id", pid)
          .select()
          .single(),
      );
    if (fallbackError) {
      const reason = extractClubPersonBillingAssignmentReason(fallbackError);
      return reason
        ? { ok: false, error: fallbackError, reason }
        : { ok: false, error: fallbackError };
    }
    await syncBillingContactFromClubPersonMirror(fallbackData);
    return { ok: true, data: fallbackData };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function deleteClubPerson(id) {
  const pid = __asStr(id);
  if (!pid) return { ok: false, error: "missing-id" };
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { error } = await supabase.from("club_people").delete().eq("id", pid);
    if (error) return { ok: false, error };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e };
  }
}
